import { StatusCodes } from 'http-status-codes';
import type { z, ZodType } from 'zod';
import type { Either } from '../common';
import { asyncEitherOf, Logger } from '../common';
import type { PlainObject } from 'simplytyped';
import { allPaths, unAuthenticatedPathUrls } from '../routes/Paths';
import { resetLocalStorageLoginData, resetLocalStorageUser } from '../contexts';
import axios from 'axios';

const commitId = process.env.REACT_APP_COMMIT_ID;

type HttpVerbs = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type RequestBody = PlainObject;

type RequestHeaders = {
  [key: string]: string | number | boolean;
};
type RequestOptions = {
  method: HttpVerbs;
  body?: any; // Todo work out a type
  headers: {
    Accept: 'application/json' | string;
    [key: string]: string;
  };
  credentials?: string;
};
type QueryParams = {
  [key: string]: string | number | boolean | Array<string | number | boolean>;
};
type AdditionalOptions = {
  [key: string]: string;
};

type AdditionalHeaders = {
  [key: string]: string;
};

type TsafeGetArgs<TData> = {
  path: string;
  schema: z.Schema<TData>;
  params?: QueryParams;
  additionalOptions?: AdditionalOptions;
  additionalHeaders?: AdditionalHeaders;
};

class HttpError extends Error {
  public status: number;
  public message: string;
  public data?: string | object;

  constructor(status: number, message: string, data?: string | object) {
    super(message);
    this.status = status;
    this.message = message;
    this.data = data;
  }
}

const wrappedFetch: typeof fetch = async (...opts) => {
  try {
    const response = await fetch(...opts);
    const isFromUnauthenticatedPath = unAuthenticatedPathUrls.includes(
      window.location.pathname as (typeof unAuthenticatedPathUrls)[number]
    );

    if (
      response.status === StatusCodes.UNAUTHORIZED &&
      !isFromUnauthenticatedPath
    ) {
      resetLocalStorageLoginData();
      resetLocalStorageUser();
      // Do not redirect on StatusCodes.FORBIDDEN
      window.location.href = allPaths.LOGIN.path;
    }

    return response;
  } catch (error) {
    // Handle network or Fetch API errors
    // TODO log
    // Logger.error({
    //   source: 'An error occurred in the request:',
    //   message: JSON.stringify(error)
    // });
    console.error('An error occurred in the request:', error);
    throw error;
  }
};

class Request {
  static baseURL = process.env.REACT_APP_MERCHANT_PORTAL_BFF_URL;

  static wrappedHeaders(headers?: RequestHeaders): RequestHeaders {
    const session = localStorage.session
      ? JSON.parse(localStorage.session)
      : {};
    return {
      'X-Build-Version': commitId,
      ...(session?.sessionId && { 'X-Session-ID': session?.sessionId }),
      ...headers
    };
  }

  static wrappedOptions(_options?: RequestOptions): RequestOptions {
    const { headers, ...options } = _options ?? {};
    return {
      ...options,
      headers: Request.wrappedHeaders(headers)
    } as RequestInit;
  }

  static async fetchHandling(path: string, options?: RequestOptions) {
    return wrappedFetch(path, Request.wrappedOptions(options))
      .then(async (response) => {
        if (response.status === 204) {
          //We don't want to try convert nothing to json
          return;
        }
        if (response.ok) {
          return response.json();
        }
        const ResponseJson: any = await response.json();
        throw new HttpError(
          response.status,
          ResponseJson.message,
          ResponseJson.data
        );
      })
      .then((responseJson) => {
        return responseJson;
      });
  }

  static async get<TData>({
    path,
    base = Request.baseURL,
    schema,
    params,
    additionalOptions,
    additionalHeaders
  }: {
    path: string;
    base?: string;
    schema: ZodType<TData>;
    params?: QueryParams;
    additionalOptions?: AdditionalOptions;
    additionalHeaders?: AdditionalHeaders;
  }) {
    const options: RequestOptions = {
      method: 'GET',
      ...additionalOptions,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json ; charset=UTF-8',
        ...additionalHeaders
      },
      credentials: 'include'
    };

    Logger.debug({ source: 'Request.ts get', message: `path: ${path}` });
    let queryString = '';
    if (params && Object.keys(params).length) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          v.forEach((val) => queryParams.append(k, `${val}`));
        } else {
          queryParams.append(k, `${v}`);
        }
      });
      queryString = `${path.includes('?') ? '&' : '?'}${queryParams}`;
    }
    const response = await Request.fetchHandling(
      `${base}/${path}${queryString}`,
      options
    ).then((res) => {
      return res;
    });
    Logger.debug({ source: 'Request.tsx', message: JSON.stringify(response) });
    const parseResult = schema.parse(response);
    // TODO log
    // Logger.info({
    //   source: 'Request.tsx ___ parseResult',
    //   message: JSON.stringify(parseResult)
    // });
    return parseResult;
  }

  static async safeGet<TData>(
    args: TsafeGetArgs<TData>
  ): Promise<Either<TData>> {
    const { path, schema, params, additionalOptions, additionalHeaders } = args;
    return asyncEitherOf(
      Request.get({
        path,
        schema,
        params,
        additionalOptions,
        additionalHeaders
      }),
      'dashboard',
      `tried to get ${path}`,
      'corredlationId: 123'
    );
  }

  static async getPdf(path: string, additionalHeaders?: AdditionalHeaders) {
    const baseUrl = Request.baseURL;
    const response = await axios({
      baseURL: baseUrl,
      method: 'GET',
      url: path,
      responseType: 'arraybuffer',
      withCredentials: true,
      headers: Request.wrappedHeaders({
        ...additionalHeaders,
        credentials: 'include',
        'Content-Type': 'application/pdf'
      })
    }).catch(function (error) {
      // Handle network or Fetch API errors
      // TODO log
      // Logger.error({
      //   source: 'An error occurred in the request:',
      //   message: JSON.stringify(error)
      // });
      console.error('An error occurred in the request:', error);
    });

    return response;
  }

  static async post<TData>(
    path: string,
    schema: z.Schema<TData>,
    body?: RequestBody,
    headers?: RequestHeaders
  ) {
    const options: RequestOptions = {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=UTF-8',
        ...headers
      },
      credentials: 'include'
    };
    Logger.debug({ source: 'Request.ts post', message: `path: ${path}` });
    const response = await Request.fetchHandling(
      `${Request.baseURL}/${path}`,
      options
    ).then((res) => {
      Logger.debug({
        source: 'Request.ts post',
        message: `res: ${JSON.stringify(res, null, 2)}`
      });
      return res;
    });

    schema.parse(response);
    Logger.debug({ source: 'Request.tsx', message: JSON.stringify(response) });
    return response;
  }

  // TODO: should make a ticket to create / use these
  // similar to in MerchantService & BFF so we can keep logic in one place
  static async safePost<TData>(
    path: string,
    schema: z.Schema<TData>,
    body?: RequestBody,
    headers?: RequestHeaders
  ): Promise<Either<TData>> {
    const base = Request.baseURL;
    return asyncEitherOf(
      Request.post(
        // Request.axiosPost({
        path,
        // base,
        schema,
        body,
        headers
      ),
      'Request.safePost',
      `tried to post baseUrl: ${base} path: ${path}`,
      'corredlationId: 123'
    );
  }

  static async put<TData>(
    path: string,
    schema: z.Schema<TData>,
    body?: RequestBody
  ) {
    const options: RequestOptions = {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=UTF-8'
      },
      credentials: 'include'
    };
    Logger.debug({ source: 'Request.ts put', message: `path: ${path}` });
    const response = await Request.fetchHandling(
      `${Request.baseURL}/${path}`,
      options
    ).then((res) => {
      Logger.debug({
        source: 'Request.ts put',
        message: `res: ${JSON.stringify(res, null, 2)}`
      });
      return res;
    });
    schema.parse(response);
    Logger.debug({ source: 'Request.tsx', message: JSON.stringify(response) });
    return response;
  }

  static async patch<TData>(
    path: string,
    schema: z.Schema<TData>,
    body: RequestBody = {}
  ) {
    const options: RequestOptions = {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=UTF-8'
      },
      credentials: 'include'
    };
    Logger.debug({ source: 'Request.ts patch', message: `path: ${path}` });
    const response = await (
      await wrappedFetch(
        `${Request.baseURL}/${path}`,
        Request.wrappedOptions(options)
      )
    ).json();
    schema.parse(response);
    Logger.debug({ source: 'Request.tsx', message: JSON.stringify(response) });
    return response;
  }
  static async delete<TData>(path: string, schema: z.Schema<TData>) {
    const options: RequestOptions = {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=UTF-8'
      },
      credentials: 'include'
    };
    Logger.debug({ source: 'Request.ts delete', message: `path: ${path}` });
    const response = await (
      await wrappedFetch(
        `${Request.baseURL}/${path}`,
        Request.wrappedOptions(options)
      )
    ).json();
    schema.parse(response);
    Logger.debug({ source: 'Request.tsx', message: JSON.stringify(response) });
    return response;
  }
}

export { Request, HttpError, type AdditionalHeaders };
